/**
 * The Zones panel — the detector's offers, the pins, and the flags (ADR-031 T1).
 *
 * THE DETECTOR OFFERS; A PERSON PINS. Allen's read #2 (2026-09-02): an
 * auto-opened flag on the boot character read as noise — "tail detector
 * should have a button — like hands do — pin tail." So this panel shows each
 * measurement (faces, elongation, who owns the flesh today) with its own pin
 * button; a zone exists only after that click, and its flag opens then. Each
 * pinned zone gets a row with `flag` (re-summon the questions on the
 * character) and ✕.
 *
 * EVERY CANDIDATE, NOT JUST THE TUBE. Read #3: "what if I also wanted to flag
 * something as cloth?" The detector had already found the tiger's coat-back
 * panels as SHEETS the first time it ran; it offered only the largest tube.
 * Now every cluster is a card — a tube offers `pin tail`, a sheet `pin
 * cloth`, a blob `pin region` — and what a person names it on the flag is
 * still theirs to decide. Cloth a bone DOES reach (a skirt skinned to the
 * thighs) is the wand's job (T3), not the detector's.
 *
 * ITS OWN PANEL, NOT A CARD, introduced once per profile (main.ts) — a
 * persisted layout predates every new surface, and a card inside a closed
 * panel is unreachable (the arm-space lesson, twice).
 *
 * A pure view over `deps`: it renders what it is handed and reports clicks.
 * Depends on: panel-registry (types). Depended on by: main.
 */
import type { PanelMount } from './panel-registry.js';
import { describeButton, withTip } from './tooltip.js';

export type CandidateKind = 'tube' | 'sheet' | 'blob' | 'stub';

export type ZonesUiState =
  | {
      readonly kind: 'ready';
      /** The detector's offers on this mesh that no zone covers yet, largest first. */
      readonly candidates: readonly {
        readonly key: number;
        readonly shape: CandidateKind;
        readonly faces: number;
        readonly elongation: number;
        readonly ownedBy: string;
      }[];
      readonly zones: readonly {
        readonly id: string;
        readonly name: string;
        readonly faces: number;
        readonly shape: CandidateKind | null;
        readonly intent: 'tail' | 'cloth' | 'softTissue' | null;
      }[];
    }
  | { readonly kind: 'refused'; readonly reason: string };

export interface ZonesPanelDeps {
  /**
   * ONE RECEIPT (XD note 2026-09-27 §3.1, PR 4): the status mark for a card's subject, read from the
   * same Body progress the section headers draw — so a card and its header can never disagree.
   * null = the object is not counted (absent).
   */
  readonly mark?: (subject: string) => HTMLElement | null;

  /** Asked at render time — the subject is replaceable. */
  readonly zones: () => ZonesUiState;
  /** Create a zone from the offered candidate and open its flag. A human act. */
  readonly pin: (key: number) => void;
  /** Re-summon a zone's flag. */
  readonly openZone: (id: string) => void;
  readonly deleteZone: (id: string) => void;
  /** Dismiss an offer for this subject (the possible stub's "not a tail"). */
  readonly dismiss: (key: number) => void;
  /**
   * A NEW ZONE BY WAND (Allen, 2026-09-25, the bear girl's bob tail the detector
   * cannot see): arm, then click the model — the patch round the click becomes a
   * zone and its flag opens. Pressed again, disarms.
   */
  readonly newZoneByWand: () => void;
  /**
   * THE BODY VOICE (XD 2026-09-29 §3B; Allen ruled the same day, "i tend to lean status"): a
   * card's READING goes to the status line on hover/focus, never onto the panel. `reveal` is for a
   * refusal only. Absent = the readings are simply not said (the card still works).
   */
  readonly say?: (line: string | null, reveal?: boolean) => void;
}

/** Say `line` whenever the pointer or focus enters `el` — the DCC status-bar idiom (XD §6 Q3). */
const speaks = (el: HTMLElement, say: ZonesPanelDeps['say'], line: () => string): void => {
  if (say === undefined) return;
  el.addEventListener('pointerenter', () => say(line()));
  el.addEventListener('focusin', () => say(line()));
};

const OFFER = {
  tube: { title: 'Tail', verb: 'pin tail', reads: 'a tube meeting the body behind the hips' },
  sheet: { title: 'Cloth', verb: 'pin cloth', reads: 'a hanging sheet no bone reaches' },
  blob: { title: 'Region', verb: 'pin region', reads: 'a region no bone reaches' },
  // The lower confidence tier (Allen, 2026-09-24: "maybe harmless to offer?"):
  // short, fat, behind the hips on the midline — a person decides.
  stub: { title: 'Possible stub tail — pin it', verb: 'pin stub tail', reads: 'a short puff behind the hips, on the midline — maybe a stub tail, maybe a pouch' },
} as const;

export function createZonesPanel(deps: ZonesPanelDeps): PanelMount {
  return (container, store) => {
    const section = document.createElement('section');
    section.className = 'zones-panel';

    const render = (): void => {
      section.replaceChildren();
      const s = deps.zones();
      if (s.kind === 'refused') {
        const refusal = document.createElement('p');
        refusal.className = 'hand-refusal';
        refusal.textContent = s.reason;
        section.append(refusal);
        return;
      }
      for (const zone of s.zones) {
        const card = document.createElement('section');
        card.className = 'hand-card zone-card';
        const title = document.createElement('h3');
        const zoneMark = deps.mark?.(zone.name) ?? null;
        if (zoneMark !== null) {
          title.textContent = zone.intent ? `${zone.name} · ${zone.intent}` : zone.name;
          title.append(zoneMark);
        } else title.textContent = `${zone.name} · ${zone.intent ? `${zone.intent} ✓` : 'unanswered'}`;
        // The reading ("1234 faces · reads as a tube") is a STATUS line now (§3B) — Creator hid it
        // with CSS already; Expert gets it on hover instead of on the tile.
        speaks(card, deps.say, () => `${zone.name}: ${zone.faces} faces${zone.shape ? ` · reads as a ${zone.shape}` : ''}`);
        const row = document.createElement('div');
        row.className = 'hand-recenter zone-row';
        const flag = document.createElement('button');
        flag.type = 'button';
        flag.className = 'hand-graft-now';
        flag.textContent = 'flag';
        flag.addEventListener('click', () => deps.openZone(zone.id));
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'hand-revert';
        remove.textContent = '✕';
        remove.setAttribute('aria-label', `remove zone ${zone.name}`);
        remove.addEventListener('click', () => deps.deleteZone(zone.id));
        row.append(...withTip(flag, 'open this zone’s questions on the character'), ...withTip(remove, 'not a zone — remove it (undo brings it back)'));
        card.append(title, row);
        section.append(card);
      }
      for (const c of s.candidates) {
        const offer = OFFER[c.shape];
        const card = document.createElement('section');
        card.className = 'hand-card zone-card zone-offer';
        const title = document.createElement('h3');
        title.textContent = `${offer.title}?`;
        const offerMark = deps.mark?.(`${offer.title}?`) ?? null;
        if (offerMark !== null) title.append(offerMark);
        // The measurement is a STATUS line (§3B); the tile keeps the question and its verb.
        speaks(card, deps.say, () => `${offer.reads[0]!.toUpperCase()}${offer.reads.slice(1)}: ${c.faces} faces, ${c.elongation.toFixed(1)}× long, owned today by ${c.ownedBy}.`);
        const pin = document.createElement('button');
        pin.type = 'button';
        pin.className = 'hand-graft-now';
        pin.textContent = offer.verb;
        pin.addEventListener('click', () => deps.pin(c.key));
        card.append(title, ...withTip(pin, 'make it a zone and ask the questions on the character'));
        if (c.shape === 'stub') {
          // Softer than a confident tail, and dismissible — the dismissal sticks for this subject.
          card.classList.add('zone-offer-tentative');
          const no = document.createElement('button');
          no.type = 'button';
          no.className = 'hand-revert';
          no.textContent = 'not a tail';
          no.dataset['dismiss'] = 'stub';
          no.addEventListener('click', () => deps.dismiss(c.key));
          card.append(...withTip(no, 'dismiss this offer on this character'));
        }
        section.append(card);
      }
      // The empty "Zones" card is DELETED (XD 2026-09-29 §3B): its only line was a reading, so
      // Creator hid it as a bare heading and Expert read a paragraph. The finding is said on the
      // wand card's hover instead (the one card such a body still shows).
    };

    // Always offered, last: the escape hatch for what the detector misses.
    const renderWandCard = (): void => {
      const card = document.createElement('section');
      card.className = 'hand-card zone-card';
      card.dataset['zoneWand'] = 'new';
      const title = document.createElement('h3');
      title.textContent = 'Missed one?';
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'hand-graft-now';
      b.textContent = '➕ new zone (wand)';
      b.addEventListener('click', () => deps.newZoneByWand());
      // The how-to is HELP, not a panel paragraph (§3B): the `?` beside the verb, the Elbows idiom.
      const head = document.createElement('div');
      head.className = 'elbow-head';
      head.append(
        ...withTip(b, 'then click the model where the zone is'),
        describeButton('the zone wand', () => 'Arm the wand and click the flesh on the model — a small patch becomes a stub tail, a longer one a tail; refine it on its flag with ➕ / ➖.'),
      );
      const s = deps.zones();
      if (s.kind === 'ready' && s.zones.length === 0 && s.candidates.length === 0) {
        speaks(card, deps.say, () => 'zones: nothing on this character reads as a tail or a hanging sheet — every region has a bone near it.');
      }
      card.append(title, head);
      section.append(card);
    };
    const renderAll = (): void => {
      render();
      if (deps.zones().kind === 'ready') renderWandCard();
    };

    container.append(section);
    const unsubscribe = store.subscribe(renderAll);
    renderAll();

    return {
      dispose(): void {
        unsubscribe();
        section.remove();
      },
    };
  };
}
